import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CircleDashed, ExternalLink, FileCheck2, Globe } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { CopyableValue } from './CopyableValue';
import { DomainRowActions } from './DomainRowActions';
import type { PlatformDomain, PlatformDomainStatus } from './types';

/** Exactly the URL Apple fetches, so the link doubles as the check. */
const wellKnownUrl = (domain: string) =>
  `https://${domain}/.well-known/apple-developer-merchantid-domain-association`;

const STATUS_META: Record<
  PlatformDomainStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; Icon: typeof CircleDashed }
> = {
  pending: { variant: 'secondary', Icon: CircleDashed },
  file_ready: { variant: 'outline', Icon: FileCheck2 },
  registered: { variant: 'default', Icon: FileCheck2 },
  failed: { variant: 'destructive', Icon: AlertCircle },
  unregistered: { variant: 'secondary', Icon: CircleDashed },
};

export function DomainsTable({ domains }: { domains: PlatformDomain[] }) {
  const { t } = useTranslation('manageApplePay');

  if (domains.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Globe />
          </EmptyMedia>
          <EmptyTitle>{t('domains.empty')}</EmptyTitle>
          <EmptyDescription>{t('domains.emptyDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('domains.columns.domain')}</TableHead>
          <TableHead>{t('domains.columns.merchant')}</TableHead>
          <TableHead>{t('domains.columns.status')}</TableHead>
          <TableHead className="text-right">{t('domains.columns.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {domains.map((row) => {
          const { variant, Icon } = STATUS_META[row.status];
          return (
            // Fragment, not a wrapper element: the error needs its own full-width
            // row. Apple's failures run to several sentences, and squeezed into
            // the domain cell they overflowed across the neighbouring columns.
            <Fragment key={row.id}>
              <TableRow className={row.lastError ? 'border-b-0' : undefined}>
                <TableCell className="align-top">
                  <div className="flex flex-wrap items-center gap-2 font-medium break-all">
                    {row.domain}
                    {row.active && (
                      <Badge variant="default" className="gap-1">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
                        {t('domains.live')}
                      </Badge>
                    )}
                  </div>

                  {/* The registration id is what a checkout page must actually
                      ship for this domain; comparing the two is how a working
                      setup is told from a broken one. A missing id is itself the
                      diagnosis, so it is stated rather than left blank. */}
                  <div className="mt-1.5 flex flex-col items-start gap-1">
                    {row.btMerchantRegistrationId ? (
                      <CopyableValue
                        label={t('domains.registrationId')}
                        value={row.btMerchantRegistrationId}
                      />
                    ) : (
                      <span className="text-destructive/80 text-xs">
                        {t('domains.registrationIdMissing')}
                      </span>
                    )}

                    <a
                      href={wellKnownUrl(row.domain)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs underline underline-offset-2"
                    >
                      {t('domains.wellKnown')}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </div>
                </TableCell>

                <TableCell className="align-top">
                  <div className="text-sm break-all">{row.partnerMerchantName}</div>
                  <div className="text-muted-foreground font-mono text-xs break-all">
                    {row.partnerInternalMerchantIdentifier}
                  </div>
                </TableCell>

                <TableCell className="align-top whitespace-nowrap">
                  <Badge variant={variant} className="gap-1.5">
                    <Icon className="h-3 w-3" />
                    {t(`domains.statuses.${row.status}`)}
                  </Badge>
                </TableCell>

                <TableCell className="align-top">
                  <DomainRowActions domain={row} />
                </TableCell>
              </TableRow>

              {row.lastError && (
                <TableRow className="hover:bg-transparent">
                  {/* The message is given a definite maximum width rather than
                      being left to size itself. Under the table's auto layout a
                      cell is as wide as its content demands, so Apple's
                      multi-sentence failures set the width of the whole table
                      and pushed every other column off-screen. A capped measure
                      wraps instead, and happens to be easier to read. */}
                  {/* `whitespace-normal` is the load-bearing class: TableCell
                      defaults to whitespace-nowrap, which disables line breaking
                      entirely — no max-width or overflow-wrap can wrap text the
                      browser is never allowed to break. */}
                  <TableCell colSpan={4} className="max-w-0 pt-0 whitespace-normal">
                    <div className="border-destructive/30 bg-destructive/5 flex max-w-[72ch] items-start gap-2 rounded-md border p-3">
                      <AlertCircle className="text-destructive mt-0.5 h-4 w-4 shrink-0" />
                      <div className="min-w-0 space-y-1">
                        <p className="text-destructive text-xs font-medium">{t('domains.lastError')}</p>
                        {/* Apple embeds unspaced domain lists and identifiers that
                            `break-words` will not split, so a single long token
                            could still widen the row on its own. */}
                        <p className="text-destructive/90 max-w-full text-xs leading-relaxed break-words [overflow-wrap:anywhere]">
                          {row.lastError}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
